import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Building2, Briefcase, Sparkles, User, type LucideIcon } from 'lucide-react';
import api from '../../lib/api';
import { type MfaFactor, useAuthStore } from '../../stores/authStore';
import { getRoleHomePath, requiresOrganizationOnboarding } from '../../lib/onboarding';
import { startSocialLogin } from '../../lib/socialAuth';
import { GamingTwoFactorPanel, LoginForm } from '../../components/ui/gaming-login';
import LandingHeroBackground from '../../components/LandingHeroBackground';
import { useDocumentDarkMode } from '../../hooks/useDocumentDarkMode';
import { rememberInviteRedirect, sanitizeAppRedirect } from '../../lib/inviteRedirect';

const REMEMBER_EMAIL_KEY = "mykol_remember_email";

const DEMO_CATEGORY_ICONS: Record<string, LucideIcon> = {
  Agency: Briefcase,
  Brand: Building2,
  KOL: User,
};

type PublicConfigResponse = {
  demoLogin:
    | { enabled: false }
    | {
        enabled: true;
        serverSideLogin?: true;
        categories: Array<{
          type: string;
          color: "blue" | "purple" | "green";
          accounts: Array<{
            email: string;
            label: string;
            description: string;
          }>;
        }>;
      };
  socialAuth: { enabled: boolean };
  nodeEnv: string;
};

function readSocialMessageFromQuery(): string {
  if (typeof window === "undefined") return "";
  const qp = new URLSearchParams(window.location.search);
  const code = qp.get("error") || "";
  const message = qp.get("message") || "";
  const normalized = message.trim();
  if (normalized) return normalized;
  switch (code) {
    case "account_not_linked":
      return "Google sign-in could not attach to an application profile—try signing in again, or register with email. New Google accounts should reach the role selection step.";
    case "social_disabled":
      return "Social sign-in is not enabled in this environment.";
    case "social_state_mismatch":
    case "social_state_expired":
    case "invalid_social_callback":
      return "Social sign-in session expired. Please try again.";
    case "social_auth_error":
      return "Social sign-in failed. Please try again.";
    default:
      return code ? `Social sign-in error: ${code}` : "";
  }
}

export default function LoginPage() {
  const isDark = useDocumentDarkMode();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, demoLogin, verifyMfa } = useAuthStore();
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showDemoAccounts, setShowDemoAccounts] = useState(true);
  const [publicConfig, setPublicConfig] = useState<PublicConfigResponse | null>(null);
  const [pendingMfaFactors, setPendingMfaFactors] = useState<MfaFactor[]>([]);
  const [pendingRemember, setPendingRemember] = useState<{ email: string; remember: boolean } | null>(null);
  const socialMessage = readSocialMessageFromQuery();

  const [defaultEmail, setDefaultEmail] = useState(() => {
    const qp = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('email')?.trim() : '';
    if (qp) return qp;
    return localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
  });

  useEffect(() => {
    const qpEmail = searchParams.get('email')?.trim();
    if (qpEmail) setDefaultEmail(qpEmail);
  }, [searchParams]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await api.get<PublicConfigResponse>("/config/public");
        if (active) setPublicConfig(data);
      } catch {
        if (active) {
          setPublicConfig({
            demoLogin: { enabled: false },
            socialAuth: { enabled: false },
            nodeEnv: "unknown",
          });
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const demoEnabled = publicConfig?.demoLogin.enabled === true;
  const demoCategories = publicConfig?.demoLogin.enabled ? publicConfig.demoLogin.categories : [];
  const socialEnabled = publicConfig?.socialAuth.enabled === true;

  const goDashboard = () => {
    const user = useAuthStore.getState().user;
    if (user) {
      const redirect = sanitizeAppRedirect(searchParams.get('redirect'));
      if (redirect) {
        navigate(redirect);
        return;
      }
      const roleDashboards: Record<string, string> = {
        AGENCY: "/agency/dashboard",
        BRAND: "/brand/dashboard",
        KOL: "/kol/dashboard",
        ADMIN: "/admin/dashboard",
      };
      if (requiresOrganizationOnboarding(user)) {
        navigate("/onboarding/organization");
        return;
      }
      navigate(roleDashboards[user.role] || getRoleHomePath(user));
    }
  };

  const persistRememberEmail = (email: string, remember: boolean) => {
    if (remember && email.trim()) {
      localStorage.setItem(REMEMBER_EMAIL_KEY, email.trim());
      setDefaultEmail(email.trim());
    } else {
      localStorage.removeItem(REMEMBER_EMAIL_KEY);
    }
  };

  const handleCredentialLogin = async (
    email: string,
    password: string,
    remember: boolean,
  ) => {
    setError("");
    setIsLoading(true);
    try {
      const result = await login(email, password);
      if (result.requiresMfa) {
        setPendingMfaFactors(result.factors);
        setPendingRemember({ email, remember });
        setError('');
        return;
      }
      persistRememberEmail(email, remember);
      goDashboard();
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : null;
      setError(msg || "Failed to login");
    } finally {
      setIsLoading(false);
    }
  };

  const handleQuickLogin = async (demoEmail: string) => {
    if (!demoEnabled) return;
    setError('');
    setIsLoading(true);
    try {
      const result = await demoLogin(demoEmail);
      if (result.requiresMfa) {
        setPendingMfaFactors(result.factors);
        setPendingRemember(null);
        setError('');
        return;
      }
      goDashboard();
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : null;
      setError(msg || 'Failed to login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialLogin = (provider: string) => {
    if (!socialEnabled) return;
    const redirect = sanitizeAppRedirect(searchParams.get('redirect'));
    if (redirect.startsWith('/invite/')) rememberInviteRedirect(redirect);
    startSocialLogin(provider, redirect || undefined);
  };

  const handleMfaVerify = async (code: string) => {
    setError('');
    setIsLoading(true);
    try {
      const factorId = pendingMfaFactors[0]?.id;
      await verifyMfa(code, factorId);
      if (pendingRemember) {
        persistRememberEmail(pendingRemember.email, pendingRemember.remember);
      }
      setPendingMfaFactors([]);
      setPendingRemember(null);
      goDashboard();
    } catch (err: unknown) {
      const msg =
        err && typeof err === "object" && "response" in err
          ? (err as { response?: { data?: { error?: string } } }).response?.data
              ?.error
          : null;
      setError(msg || 'Failed to verify MFA code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleMfaBack = () => {
    setPendingMfaFactors([]);
    setPendingRemember(null);
    setError('');
  };

  const getColorClasses = (color: string) => {
    const colors: Record<
      string,
      { bg: string; border: string; text: string; hover: string; icon: string }
    > = {
      blue: {
        bg: "bg-accent2/10",
        border: "border-accent2/30",
        text: "text-accent2",
        hover: "hover:bg-accent2/15",
        icon: "text-accent2",
      },
      purple: {
        bg: "bg-cyan-500/10 dark:bg-cyan-400/10",
        border: "border-cyan-500/30",
        text: "text-cyan-600 dark:text-cyan-300",
        hover: "hover:bg-cyan-500/15",
        icon: "text-cyan-600 dark:text-cyan-300",
      },
      green: {
        bg: "bg-emerald-500/10",
        border: "border-emerald-500/30",
        text: "text-emerald-600 dark:text-emerald-300",
        hover: "hover:bg-emerald-500/15",
        icon: "text-emerald-600 dark:text-emerald-300",
      },
    };
    return colors[color];
  };

  return (
    <div className="theme-agency relative min-h-screen w-full overflow-x-hidden px-4 py-10 text-glass-text login-glass-bg">
      {/* Animated mesh / particles / ribbons / streaks — does not replace login-glass-bg */}
      <div className="pointer-events-none fixed inset-0 z-0">
        <LandingHeroBackground
          appearance={isDark ? "dark" : "light"}
          layerMode="login-overlay"
        />
      </div>

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-col items-center gap-10 pb-16">
        {socialMessage && (
          <div className="w-full max-w-md rounded-xl border border-amber-400/40 bg-amber-400/10 p-3 text-sm text-amber-700 dark:text-amber-200">
            {socialMessage}
          </div>
        )}
        <div className="w-full max-w-md animate-fade-in">
          {pendingMfaFactors.length > 0 ? (
            <GamingTwoFactorPanel
              onVerify={handleMfaVerify}
              onBack={handleMfaBack}
              isLoading={isLoading}
              error={error || null}
            />
          ) : (
            <LoginForm
              onLogin={handleCredentialLogin}
              onSocialLogin={handleSocialLogin}
              isLoading={isLoading}
              error={error || null}
              defaultEmail={defaultEmail}
              socialLoginEnabled={socialEnabled}
            />
          )}
        </div>

        {pendingMfaFactors.length === 0 && demoEnabled && showDemoAccounts && publicConfig && (
          <div className="glass-card w-full p-6 shadow-xl">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-accent2" />
                <h3 className="text-lg font-semibold text-glass-text">Quick demo login</h3>
              </div>
            </div>

            <p className="mb-4 text-sm text-glass-text-2">
              Pick an account to start a demo session. The shared demo credential stays on the API server.
            </p>

            <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
              {demoCategories.map((category) => {
                const Icon = DEMO_CATEGORY_ICONS[category.type] || User;
                const colors = getColorClasses(category.color);

                return (
                  <div
                    key={category.type}
                    className={`rounded-xl border ${colors.border} ${colors.bg} p-4`}
                  >
                    <div className="mb-3 flex items-center gap-2">
                      <Icon className={`h-5 w-5 ${colors.icon}`} />
                      <h4 className={`font-semibold ${colors.text}`}>{category.type}</h4>
                    </div>

                    <div className="max-h-64 space-y-2 overflow-y-auto glass-scroll pr-1">
                      {category.accounts.map((account) => (
                        <button
                          key={account.email}
                          type="button"
                          onClick={() => handleQuickLogin(account.email)}
                          disabled={isLoading}
                          className={`w-full rounded-lg border border-glass bg-glass-2 p-3 text-left transition-colors hover:bg-glass-3 disabled:cursor-not-allowed disabled:opacity-50 ${colors.hover}`}
                        >
                          <div className="text-sm font-medium text-glass-text">{account.label}</div>
                          <div className="mt-0.5 text-xs text-glass-text-3">{account.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {pendingMfaFactors.length === 0 && demoEnabled && !showDemoAccounts && (
          <button
            type="button"
            onClick={() => setShowDemoAccounts(true)}
            className="text-sm font-medium text-accent2 hover:underline"
          >
            Show demo accounts
          </button>
        )}

        <footer className="text-center text-sm text-glass-text-3">
          © {new Date().getFullYear()} QueueMart 1260221-X All rights reserved.
          ·{" "}
          <Link to="/" className="text-accent2 hover:underline">
            Home
          </Link>
        </footer>
      </div>
    </div>
  );
}
